import { z } from '@repo/schema'

/** A Google lookup either finds a value, finds nothing, or fails to answer. */
export type LookupResult<T> =
  | { error: unknown; status: 'failed' }
  | { status: 'empty' }
  | { status: 'found'; value: T }

const googleStatusSchema = z.object({ status: z.string().optional() })
const EMPTY_GOOGLE_STATUSES = new Set(['NOT_FOUND', 'ZERO_RESULTS'])

/**
 * Fetches a geolocation service URL; `toValue` returns null when the answer is empty.
 * Google statuses other than OK, ZERO_RESULTS and NOT_FOUND (quota, key, request errors) fail.
 */
export const fetchLookup = async <TSchema extends z.ZodType, T>(
  url: string,
  schema: TSchema,
  toValue: (_data: z.output<TSchema>) => null | T,
  signal?: AbortSignal,
): Promise<LookupResult<T>> => {
  try {
    const response = await fetch(url, { headers: { Accept: 'application/json' }, signal })

    if (!response.ok) {
      return { error: new Error(`${response.status} ${response.statusText}`), status: 'failed' }
    }

    const body: unknown = await response.json()
    const { status } = googleStatusSchema.parse(body)
    if (status && EMPTY_GOOGLE_STATUSES.has(status)) return { status: 'empty' }
    if (status && status !== 'OK') {
      return { error: new Error(`Google status ${status}`), status: 'failed' }
    }

    const value = toValue(schema.parse(body))

    return value === null ? { status: 'empty' } : { status: 'found', value }
  } catch (error) {
    // A cancelled query is not a failed lookup; let React Query discard it.
    if (signal?.aborted) throw error

    return { error, status: 'failed' }
  }
}

/** Query hooks show a failed lookup as no results. */
export const toList = <T>(result: LookupResult<T[]>): T[] => {
  if (result.status === 'failed') console.warn(result.error)

  return result.status === 'found' ? result.value : []
}
